import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { useEffect } from 'react';

import InfoIcon from '@mui/icons-material/Info';
import { Grid, InputAdornment, Tooltip } from '@mui/material';

import { Job } from '@libs/graphql-types';
import { modelPath } from '@libs/helpers/form';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';
import Typography from '@libs/ui/components/typography';

import { generateDefaultSecurityId } from '../../security-id-utils';
import { ApplicationFormState } from '../../types';
import { BirthDateFields } from './BirthDateFields';

interface DefaultSocialSecurityIdProps {
  job: Job;
  showOnlyRequired?: boolean;
}

/**
 * Hook to automatically sync the security ID field when separate components change
 */
export const useSecurityIdSync = () => {
  const { values, setFieldValue } = useFormikContext<ApplicationFormState>();

  useEffect(() => {
    const { firstName } = values.basicInfo;
    const { monthOfBirth, dayOfBirth, ssnLast4Digits } = values.details;

    // Only generate if all required fields are present
    if (firstName && monthOfBirth > 0 && dayOfBirth > 0 && ssnLast4Digits) {
      const securityId = generateDefaultSecurityId(
        firstName,
        monthOfBirth,
        dayOfBirth,
        ssnLast4Digits,
      );

      // Update both security ID and confirm security ID fields
      if (securityId !== values.details.securityId) {
        setFieldValue('details.securityId', securityId);
        setFieldValue('details.confirmSecurityId', securityId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.basicInfo.firstName,
    values.details.monthOfBirth,
    values.details.dayOfBirth,
    values.details.ssnLast4Digits,
    values.details.securityId,
    setFieldValue,
  ]);
};

/**
 * Default Social Security ID form component
 * This component renders the standard form for most job applications
 * using only the last 4 digits of SSN
 */
export const DefaultSocialSecurityId: React.FC<
  DefaultSocialSecurityIdProps
> = ({ job, showOnlyRequired = false }) => {
  useSecurityIdSync();
  const { t } = useTranslation('jobForm');

  return (
    <>
      {/* Separate fields for Security ID components */}
      <Grid item>
        <Typography variant="h6" gutterBottom>
          {t('applicationForm.socialSecurity.title')}{' '}
          <Typography component="span" color="error">
            *
          </Typography>
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {t('applicationForm.socialSecurity.formatDescription')}
        </Typography>
      </Grid>

      <Grid item>
        <BirthDateFields showOnlyRequired={showOnlyRequired} />
      </Grid>

      <Grid item>
        <ConnectedTextField
          name={modelPath<ApplicationFormState>(
            (i) => i.details.ssnLast4Digits,
          )}
          fullWidth
          variant="filled"
          label={t('applicationForm.socialSecurity.last4SSN')}
          placeholder="0316"
          inputProps={{ maxLength: 4 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={t('applicationForm.socialSecurity.last4Tooltip')}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item>
        <ConnectedTextField
          name={modelPath<ApplicationFormState>((i) => i.details.securityId)}
          fullWidth
          variant="filled"
          label={t('applicationForm.socialSecurity.securityId')}
          disabled
          placeholder="FFFMMDDNNNN"
          inputProps={{ maxLength: 11 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={t('applicationForm.socialSecurity.securityId')}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    </>
  );
};
